import { useEffect, useRef, useState } from 'react';

function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function useHover() {
  const [hovered, setHovered] = useState(false);
  return {
    hovered,
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
  };
}

export default function RepertoireSection() {
  const { ref: headerRef, visible: headerVisible } = useReveal(0.2);
  const { ref: gridRef, visible: gridVisible } = useReveal(0.1);

  const hover0 = useHover();
  const hover1 = useHover();
  const hover2 = useHover();
  const hover3 = useHover();
  const hover4 = useHover();
  const hover5 = useHover();
  const hovers = [hover0, hover1, hover2, hover3, hover4, hover5];

  const repertoires = [
    {
      composer: 'Adamo',
      opera: 'Lysistrata',
      role: 'Kinesias',
      bgImage: 'https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/7b6086f8a19a5c6bafa51dd4c74e6b92.jpeg',
      type: 'image'
    },
    {
      composer: 'Mozart',
      opera: 'Don Giovanni',
      role: 'Don Giovanni',
      bgImage: 'https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/15439783d9ffc24fadf54b43f9c3d15f.jpeg',
      type: 'image'
    },
    {
      composer: 'Bizet',
      opera: 'Carmen',
      role: 'Escamillo',
      bgImage: 'https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/4981ccf658d6c89b5eea13302f524947.jpeg',
      type: 'image'
    },
    {
      composer: 'Mozart',
      opera: 'Le Nozze di Figaro',
      role: 'Il Conte Almaviva',
      bgImage: 'https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/b12753acb2c80a2a310e638fc415bd73.jpeg',
      portrait: 'https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/2f95d75eda1030c959c197ee78f66c1e.jpeg',
      bgColor: 'bg-gradient-to-br from-champagne-gold/30 to-champagne-gold/10',
      type: 'large'
    },
    {
      composer: 'Puccini',
      opera: 'La Bohème',
      role: 'Marcello',
      bgImage: 'https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/e2fc59cbff37e6c89aecec9e2cde4c0d.jpeg',
      type: 'image'
    },
    {
      composer: 'Leoncavallo',
      opera: 'I Pagliacci',
      role: 'Silvio',
      bgImage: 'https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/502cbdeb16167d76d5cc159ad1c5a788.jpeg',
      type: 'image'
    }
  ];

  const cardDelays = [0, 0.18, 0.36, 0.54, 0.72, 0.9];

  const cardStyle = (idx: number, hovered: boolean): React.CSSProperties => ({
    opacity: gridVisible ? 1 : 0,
    transform: gridVisible
      ? hovered ? 'scale(1.05) translateY(0px)' : 'scale(1) translateY(0px)'
      : 'scale(0.93) translateY(28px)',
    transition: `opacity 0.9s cubic-bezier(0.22,1,0.36,1) ${cardDelays[idx]}s, transform 0.9s cubic-bezier(0.22,1,0.36,1) ${gridVisible ? '0s' : `${cardDelays[idx]}s`}, box-shadow 0.3s ease`,
    boxShadow: hovered ? '0 25px 50px rgba(0,0,0,0.5)' : undefined,
  });

  return (
    <section id="repertoire" className="relative overflow-hidden py-32 px-6 bg-opera-black">
      {/* 顶部渐变：与 About 区底部平滑衔接 */}
      <div
        className="absolute top-0 left-0 right-0 z-[1] pointer-events-none"
        style={{
          height: '120px',
          background: 'linear-gradient(to bottom, #0a0a0a 0%, transparent 100%)',
        }}
      />
      {/* 底部渐变：与 Bernstein Quote 区平滑衔接 */}
      <div
        className="absolute bottom-0 left-0 right-0 z-[1] pointer-events-none"
        style={{
          height: '120px',
          background: 'linear-gradient(to top, #0a0a0a 0%, transparent 100%)',
        }}
      />
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div
          ref={headerRef}
          className="text-center mb-10 md:mb-16"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0px)' : 'translateY(32px)',
            transition: 'opacity 1.1s cubic-bezier(0.22,1,0.36,1), transform 1.1s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          <div
            className="inline-flex items-center gap-2 px-4 py-2 border border-champagne-gold/40 rounded-sm mb-4 md:mb-6"
            style={{
              opacity: headerVisible ? 1 : 0,
              transition: 'opacity 1s ease 0.2s',
            }}
          >
            <i className="ri-music-2-line text-sm" style={{ color: '#c9a84c' }}></i>
            <span className="font-cormorant text-xs tracking-widest" style={{ color: '#c9a84c', textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>REPERTOIRE</span>
          </div>
          <h2
            className="text-2xl md:text-4xl font-cinzel-decorative font-bold mb-3 md:mb-4"
            style={{
              color: '#c9a84c',
              textShadow: '0 2px 10px rgba(0,0,0,0.7)',
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0px)' : 'translateY(20px)',
              transition: 'opacity 1.1s cubic-bezier(0.22,1,0.36,1) 0.3s, transform 1.1s cubic-bezier(0.22,1,0.36,1) 0.3s',
            }}
          >
            Signature Roles and Scenes
          </h2>
          <p
            className="font-cormorant text-sm md:text-lg px-2"
            style={{
              color: '#ede8d8',
              opacity: headerVisible ? 0.7 : 0,
              textShadow: '0 1px 4px rgba(0,0,0,0.5)',
              transform: headerVisible ? 'translateY(0px)' : 'translateY(16px)',
              transition: 'opacity 1.1s cubic-bezier(0.22,1,0.36,1) 0.55s, transform 1.1s cubic-bezier(0.22,1,0.36,1) 0.55s',
            }}
          >
            Selected roles from the masterworks of&nbsp; Mozart, Puccini, Donizetti, Verdi and beyond
          </p>
        </div>

        {/* Grid Layout */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {/* First Row */}
          {repertoires.slice(0, 3).map((item, index) => (
            <div
              key={index}
              className="relative overflow-hidden rounded-lg p-6 md:p-8 group"
              style={{
                backgroundImage: `url(${item.bgImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                minHeight: '180px',
                ...cardStyle(index, hovers[index].hovered),
              }}
              onMouseEnter={hovers[index].onMouseEnter}
              onMouseLeave={hovers[index].onMouseLeave}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-deep-black/70 via-deep-black/35 to-deep-black/10"></div>
              <div className="relative">
                <div className="inline-block px-2 py-1 bg-champagne-gold/20 border border-champagne-gold/40 rounded-sm mb-3">
                  <span className="font-cormorant text-xs tracking-wider font-bold" style={{ color: '#c9a84c', textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>{item.composer}</span>
                </div>
                <h3 className="text-2xl md:text-4xl font-playfair font-bold mb-1 md:mb-2 leading-tight" style={{ color: '#ede8d8', textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,0.8)' }}>
                  {item.opera}
                </h3>
                <p className="font-cormorant text-base md:text-lg font-bold" style={{ color: '#ede8d8', textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}>{item.role}</p>
                <div className="mt-4 h-px w-12 bg-champagne-gold/60"></div>
              </div>
            </div>
          ))}

          {/* Second Row - Large Card */}
          <div
            className="md:col-span-2"
            style={cardStyle(3, hovers[3].hovered)}
            onMouseEnter={hovers[3].onMouseEnter}
            onMouseLeave={hovers[3].onMouseLeave}
          >
            <div
              className="relative rounded-lg p-5 md:p-10 h-full flex items-center gap-3 md:gap-8 group overflow-hidden"
              style={{
                backgroundImage: `url(${repertoires[3].bgImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                minHeight: '160px',
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-deep-black/70 via-deep-black/35 to-deep-black/10"></div>
              <div className="relative flex items-center gap-3 md:gap-8 w-full">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 md:w-32 md:h-32 rounded-full overflow-hidden border-2 md:border-4 border-champagne-gold/40">
                    <img
                      src={repertoires[3].portrait}
                      alt={repertoires[3].opera}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div>
                  <div className="inline-block px-2 py-1 bg-deep-black/40 border border-champagne-gold/40 rounded-sm mb-2 md:mb-3">
                    <span className="font-cormorant text-xs tracking-wider font-bold" style={{ color: '#c9a84c', textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
                      {repertoires[3].composer}
                    </span>
                  </div>
                  <h3 className="text-xl md:text-5xl font-playfair font-bold mb-1 md:mb-2 leading-tight" style={{ color: '#ede8d8', textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,0.8)' }}>
                    {repertoires[3].opera}
                  </h3>
                  <p className="font-cormorant text-sm md:text-xl font-bold" style={{ color: '#ede8d8', textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}>{repertoires[3].role}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 md:gap-6">
            {repertoires.slice(4, 6).map((item, index) => (
              <div
                key={index}
                className="relative rounded-lg p-5 md:p-6 flex-1 flex flex-col justify-center items-center text-center group overflow-hidden"
                style={{
                  backgroundImage: `url(${item.bgImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  minHeight: '130px',
                  ...cardStyle(4 + index, hovers[4 + index].hovered),
                }}
                onMouseEnter={hovers[4 + index].onMouseEnter}
                onMouseLeave={hovers[4 + index].onMouseLeave}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-deep-black/70 via-deep-black/35 to-deep-black/10"></div>
                <div className="relative z-10 flex flex-col items-center">
                  <div className="inline-block px-2 py-1 bg-champagne-gold/10 border border-champagne-gold/40 rounded-sm mb-2">
                    <span className="font-cormorant text-xs tracking-wider font-bold" style={{ color: '#c9a84c', textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>{item.composer}</span>
                  </div>
                  <h3 className="text-xl md:text-3xl font-playfair font-bold mb-1 leading-tight" style={{ color: '#ede8d8', textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,0.8)' }}>
                    {item.opera}
                  </h3>
                  <p className="font-cormorant text-sm md:text-base font-bold" style={{ color: '#ede8d8', textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}>{item.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
